import { graphqlClient } from '@/utils/graphqlClient';
import { gql } from 'graphql-request';
import isEqual from 'lodash/isEqual';

interface ExtractResponse {
  extract: {
    slug: string;
    timestamp: string;
    json: string;
    versions: string[];
  } | null;
}

interface ExtractsResponse {
  extracts: string[];
}

/**
 * Compare two objects and determine if they are different
 */
export const hasObjectDifferences = (obj1: any, obj2: any): boolean => {
  // Use only the detailed diff algorithm to ensure consistency
  // between yellow/green indicators and the details shown
  try {
    // Our main comparison function that produces itemized changes
    const differences = findDifferences(obj1, obj2);
    return differences.totalChanges > 0;
  } catch (e) {
    console.error('Error in hasObjectDifferences:', e);
    // If we can't compare properly, assume there are no differences
    // This prevents showing yellow when we can't itemize
    return false;
  }
};

/**
 * Find all differences between two objects
 * Returns detailed information about what changed
 */
const findDifferences = (obj1: any, obj2: any): {
  added: string[];
  removed: string[];
  modified: string[];
  addedCount: number;
  removedCount: number;
  modifiedCount: number;
  totalChanges: number;
} => {
  const added: string[] = [];
  const modified: string[] = [];
  const removed: string[] = [];

  // Skip comparison for identical objects
  if (obj1 === obj2) {
    return {
      added,
      modified,
      removed,
      addedCount: 0,
      modifiedCount: 0,
      removedCount: 0,
      totalChanges: 0
    };
  }
  
  // Compare objects recursively
  compareObjects(obj1, obj2, '');
  
  function compareObjects(a: any, b: any, path: string) {
    // Handle null/undefined cases
    if (a === null && b === null) return;
    if (a === undefined && b === undefined) return;
    
    // One is null/undefined but the other isn't
    if (a === null || a === undefined) {
      removed.push(path || 'root');
      return;
    }
    if (b === null || b === undefined) {
      added.push(path || 'root');
      return;
    }
    
    // Different types means modification
    if (typeof a !== typeof b) {
      modified.push(path || 'root');
      return;
    }
    
    // Handle arrays
    if (Array.isArray(a) && Array.isArray(b)) {
      // Check if we can match by ID
      if (a.length > 0 && b.length > 0 && 
          typeof a[0] === 'object' && a[0] !== null &&
          typeof b[0] === 'object' && b[0] !== null) {
            
        const hasId = Object.prototype.hasOwnProperty.call(a[0], 'id') || Object.prototype.hasOwnProperty.call(a[0], '_id') ||
                      Object.prototype.hasOwnProperty.call(b[0], 'id') || Object.prototype.hasOwnProperty.call(b[0], '_id');
                      
        if (hasId) {
          // Use ID-based comparison for arrays of objects with IDs
          const idKey = Object.prototype.hasOwnProperty.call(a[0], 'id') ? 'id' : '_id';
          const aMap = new Map();
          const bMap = new Map();
          
          // Build lookup maps
          a.forEach((item: any) => {
            if (item && typeof item === 'object' && item[idKey] !== undefined) {
              aMap.set(item[idKey], item);
            }
          });
          
          b.forEach((item: any) => {
            if (item && typeof item === 'object' && item[idKey] !== undefined) {
              bMap.set(item[idKey], item);
            }
          });
          
          // Find added items
          for (const [id, item] of aMap.entries()) {
            if (!bMap.has(id)) {
              const label = path ? `${path}.${item.name || id}` : (item.name || id);
              added.push(label);
            }
          }
          
          // Find removed items
          for (const [id, item] of bMap.entries()) {
            if (!aMap.has(id)) {
              const label = path ? `${path}.${item.name || id}` : (item.name || id);
              removed.push(label);
            }
          }
          
          // Compare matching items
          for (const [id, itemA] of aMap.entries()) {
            const itemB = bMap.get(id);
            if (itemB) {
              const itemPath = path ? `${path}.${itemA.name || id}` : (itemA.name || id);
              compareObjects(itemA, itemB, itemPath);
            }
          }
          
          return;
        }
      }
      
      // Simple array comparison
      if (a.length !== b.length) {
        modified.push(`${path || 'Array'} (length changed from ${b.length} to ${a.length})`);
      } else {
        // Compare each element
        for (let i = 0; i < a.length; i++) {
          compareObjects(a[i], b[i], path ? `${path}[${i}]` : `[${i}]`);
        }
      }
      
      return;
    }
    
    // Handle objects
    if (typeof a === 'object' && typeof b === 'object') {
      const allKeys = new Set([...Object.keys(a), ...Object.keys(b)]);
      
      for (const key of allKeys) {
        const newPath = path ? `${path}.${key}` : key;
        
        if (!(key in b)) {
          added.push(newPath);
        } else if (!(key in a)) {
          removed.push(newPath);
        } else {
          compareObjects(a[key], b[key], newPath);
        }
      }
      
      return;
    }
    
    // Handle primitive values
    if (a !== b) {
      modified.push(path || 'value');
    }
  }
  
  // Format paths for readability
  const formatPathForDisplay = (path: string): string => {
    if (!path) return 'Root data';
    
    // Convert dot notation to more readable form
    const parts = path.split('.');
    if (parts.length === 1) return path.charAt(0).toUpperCase() + path.slice(1);
    
    // Handle common paths specially
    if (parts[0] === 'company') {
      if (parts.length === 1) return 'Company details';
      return `Company ${parts[1]}`;
    }
    
    if (parts[0] === 'plans') {
      return `Plan: ${parts[1]}`;
    }
    
    if (parts[0] === 'features') {
      return `Feature: ${parts[1]}`;
    }
    
    return path;
  };
  
  // Format all paths
  const formattedAdded = added.map(formatPathForDisplay);
  const formattedModified = modified.map(formatPathForDisplay);
  const formattedRemoved = removed.map(formatPathForDisplay);
  
  // Remove duplicates
  const uniqueAdded = [...new Set(formattedAdded)];
  const uniqueModified = [...new Set(formattedModified)];
  const uniqueRemoved = [...new Set(formattedRemoved)];
  
  return {
    added: uniqueAdded,
    modified: uniqueModified,
    removed: uniqueRemoved,
    addedCount: uniqueAdded.length,
    modifiedCount: uniqueModified.length,
    removedCount: uniqueRemoved.length,
    totalChanges: uniqueAdded.length + uniqueModified.length + uniqueRemoved.length
  };
};

/**
 * Enhanced comparison interfaces
 */
interface ExtractEntityChange<T> {
  id: string;
  name?: string; // Display name or identifier
  path: string; // Path to the entity in the schema
  previousValue: T | null;
  currentValue: T | null;
  changeType: 'added' | 'removed' | 'modified';
}

interface ExtractCompanyChange {
  id: string;
  field: string;
  previousValue: any;
  currentValue: any;
}

interface ExtractPricingMetricChange extends ExtractEntityChange<any> {
  displayName: string;
}

interface ExtractPlanChange extends ExtractEntityChange<any> {
  planName: string;
}

interface ExtractChargeChange extends ExtractEntityChange<any> {
  planId: string;
  planName: string;
  chargeType?: string;
  versionChange?: {
    added: string[];
    removed: string[];
  };
}

export interface StructuredExtractDiff {
  companyChanges: ExtractCompanyChange[];
  planChanges: ExtractPlanChange[];
  metricChanges: ExtractPricingMetricChange[];
  chargeChanges: ExtractChargeChange[];
  pageChanges: ExtractEntityChange<any>[];
  tagChanges: ExtractEntityChange<any>[];
  summary: {
    hasCompanyChanges: boolean;
    hasPlansChanges: boolean;
    hasMetricsChanges: boolean;
    hasChargesChanges: boolean;
    hasPageChanges: boolean;
    hasTagChanges: boolean;
    totalChanges: number;
  };
}

/**
 * Compare two extracts in a structured way following the company schema
 * @param current The current extract (usually from GraphQL data)
 * @param previous The previous extract (usually from published extract)
 * @returns A structured diff result that categorizes changes by entity type
 */
export const compareExtracts = (current: any, previous: any): StructuredExtractDiff => {
  // Initialize result structure
  const result: StructuredExtractDiff = {
    companyChanges: [],
    planChanges: [],
    metricChanges: [],
    chargeChanges: [],
    pageChanges: [],
    tagChanges: [],
    summary: {
      hasCompanyChanges: false,
      hasPlansChanges: false,
      hasMetricsChanges: false,
      hasChargesChanges: false,
      hasPageChanges: false,
      hasTagChanges: false,
      totalChanges: 0
    }
  };

  // Handle null/undefined cases
  if (!current || !previous) {
    return result;
  }

  // Normalize data to handle potential differences in structure
  const currentCompany = current.company || current;
  const previousCompany = previous.company || previous;

  // Compare top-level company fields (slug, employees, category, description, url, etc.)
  const basicCompanyFields = ['slug', 'employees', 'category', 'logo_url', 'description', 'url', 'scraper'];
  
  basicCompanyFields.forEach(field => {
    const currentValue = currentCompany[field];
    const previousValue = previousCompany[field];
    
    if (!isEqual(currentValue, previousValue)) {
      result.companyChanges.push({
        id: field,
        field,
        previousValue,
        currentValue
      });
    }
  });

  // Update company summary flag
  result.summary.hasCompanyChanges = result.companyChanges.length > 0;

  // Compare pages
  const currentPages = currentCompany.pages || [];
  const previousPages = previousCompany.pages || [];

  // Create maps for faster lookup
  const currentPageMap = new Map(currentPages.map((page: any) => [page.id, page]));
  const previousPageMap = new Map(previousPages.map((page: any) => [page.id, page]));
  
  // Find added and modified pages
  currentPages.forEach((currentPage: any) => {
    const previousPage = previousPageMap.get(currentPage.id);
    
    if (!previousPage) {
      // Page was added
      result.pageChanges.push({
        id: currentPage.id,
        name: currentPage.subslug || 'Main page',
        path: `pages.${currentPage.id}`,
        previousValue: null,
        currentValue: currentPage,
        changeType: 'added'
      });
    } else {
      // Compare page properties
      const pageFields = ['subslug', 'pricing_page_url'];
      
      pageFields.forEach(field => {
        if (!isEqual(currentPage[field], previousPage[field])) {
          result.pageChanges.push({
            id: `${currentPage.id}-${field}`,
            name: currentPage.subslug || 'Main page',
            path: `pages.${currentPage.id}.${field}`,
            previousValue: previousPage[field],
            currentValue: currentPage[field],
            changeType: 'modified'
          });
        }
      });
    }
  });
  
  // Find removed pages
  previousPages.forEach((previousPage: any) => {
    if (!currentPageMap.has(previousPage.id)) {
      result.pageChanges.push({
        id: previousPage.id,
        name: previousPage.subslug || 'Main page',
        path: `pages.${previousPage.id}`,
        previousValue: previousPage,
        currentValue: null,
        changeType: 'removed'
      });
    }
  });
  
  result.summary.hasPageChanges = result.pageChanges.length > 0;
  
  // Collect all pricing metrics across pages
  const collectMetricsFromPages = (pages: any[]): Map<string, any> => {
    const metricsMap = new Map();
    pages.forEach((page: any) => {
      const metrics = page.pricing_metrics || [];
      metrics.forEach((metric: any) => {
        metricsMap.set(metric.id, { ...metric, pageId: page.id });
      });
    });
    return metricsMap;
  };
  
  const currentMetricsMap = collectMetricsFromPages(currentPages);
  const previousMetricsMap = collectMetricsFromPages(previousPages);
  
  // Find added and modified metrics
  currentMetricsMap.forEach((currentMetric: any, metricId: string) => {
    const previousMetric = previousMetricsMap.get(metricId);
    
    if (!previousMetric) {
      // Metric was added
      result.metricChanges.push({
        id: metricId,
        displayName: currentMetric.display_name,
        path: `pages.${currentMetric.pageId}.pricing_metrics.${metricId}`,
        previousValue: null,
        currentValue: currentMetric,
        changeType: 'added'
      });
    } else {
      // Compare metric properties
      const metricFields = ['display_name', 'modality', 'sample_values'];
      
      metricFields.forEach(field => {
        if (!isEqual(currentMetric[field], previousMetric[field])) {
          result.metricChanges.push({
            id: `${metricId}-${field}`,
            displayName: currentMetric.display_name,
            path: `pages.${currentMetric.pageId}.pricing_metrics.${metricId}.${field}`,
            previousValue: previousMetric[field],
            currentValue: currentMetric[field],
            changeType: 'modified'
          });
        }
      });
    }
  });
  
  // Find removed metrics
  previousMetricsMap.forEach((previousMetric: any, metricId: string) => {
    if (!currentMetricsMap.has(metricId)) {
      result.metricChanges.push({
        id: metricId,
        displayName: previousMetric.display_name,
        path: `pages.${previousMetric.pageId}.pricing_metrics.${metricId}`,
        previousValue: previousMetric,
        currentValue: null,
        changeType: 'removed'
      });
    }
  });
  
  result.summary.hasMetricsChanges = result.metricChanges.length > 0;
  
  // Collect all plans across pages
  const collectPlansFromPages = (pages: any[]): Map<string, any> => {
    const plansMap = new Map();
    pages.forEach((page: any) => {
      const plans = page.plans || [];
      plans.forEach((plan: any) => {
        plansMap.set(plan.id, { ...plan, pageId: page.id });
      });
    });
    return plansMap;
  };
  
  const currentPlansMap = collectPlansFromPages(currentPages);
  const previousPlansMap = collectPlansFromPages(previousPages);
  
  // Find added and modified plans
  currentPlansMap.forEach((currentPlan: any, planId: string) => {
    const previousPlan = previousPlansMap.get(planId);
    
    if (!previousPlan) {
      // Plan was added
      result.planChanges.push({
        id: planId,
        planName: currentPlan.display_name || currentPlan.plan_name || 'Unnamed plan',
        path: `pages.${currentPlan.pageId}.plans.${planId}`,
        previousValue: null,
        currentValue: currentPlan,
        changeType: 'added'
      });
    } else {
      // Compare plan properties
      const planFields = ['display_name', 'plan_name', 'add_on'];
      
      planFields.forEach(field => {
        if (!isEqual(currentPlan[field], previousPlan[field])) {
          result.planChanges.push({
            id: `${planId}-${field}`,
            planName: currentPlan.display_name || currentPlan.plan_name || 'Unnamed plan',
            path: `pages.${currentPlan.pageId}.plans.${planId}.${field}`,
            previousValue: previousPlan[field],
            currentValue: currentPlan[field],
            changeType: 'modified'
          });
        }
      });
      
      // Compare charges within the plan
      compareCharges(
        currentPlan, 
        previousPlan, 
        result, 
        `pages.${currentPlan.pageId}.plans.${planId}`,
        currentCompany,
        previousCompany
      );
    }
  });
  
  // Find removed plans
  previousPlansMap.forEach((previousPlan: any, planId: string) => {
    if (!currentPlansMap.has(planId)) {
      result.planChanges.push({
        id: planId,
        planName: previousPlan.display_name || previousPlan.plan_name || 'Unnamed plan',
        path: `pages.${previousPlan.pageId}.plans.${planId}`,
        previousValue: previousPlan,
        currentValue: null,
        changeType: 'removed'
      });
    }
  });
  
  result.summary.hasPlansChanges = result.planChanges.length > 0;
  
  // Update total changes count
  result.summary.totalChanges = 
    result.companyChanges.length +
    result.pageChanges.length +
    result.metricChanges.length +
    result.planChanges.length +
    result.chargeChanges.length;
  
  return result;
};

/**
 * Helper function to compare charges within a plan
 * @param currentPlan The current plan from GraphQL/Redux
 * @param previousPlan The previous plan from the latest extract
 * @param result The result object to add changes to
 * @param basePath The base path for the change location
 * @param currentCompany The full current company object (needed for page-level version references)
 * @param previousCompany The full previous company object (needed for page-level version references)
 */
const compareCharges = (
  currentPlan: any, 
  previousPlan: any, 
  result: StructuredExtractDiff, 
  basePath: string, 
  currentCompany?: any,
  previousCompany?: any
) => {
  const currentCharges = currentPlan.charges || [];
  const previousCharges = previousPlan.charges || [];
  
  const currentChargeMap = new Map(currentCharges.map((charge: any) => [charge.id, charge]));
  const previousChargeMap = new Map(previousCharges.map((charge: any) => [charge.id, charge]));
  
  // Find added and modified charges
  currentCharges.forEach((currentCharge: any) => {
    const previousCharge = previousChargeMap.get(currentCharge.id);
    
    if (!previousCharge) {
      // Charge was added
      result.chargeChanges.push({
        id: currentCharge.id,
        planId: currentPlan.id,
        planName: currentPlan.display_name || currentPlan.plan_name || 'Unnamed plan',
        chargeType: currentCharge.type,
        path: `${basePath}.charges.${currentCharge.id}`,
        previousValue: null,
        currentValue: currentCharge,
        changeType: 'added'
      });
    } else {
      // Compare charge properties
      const chargeFields = ['type', 'currency', 'price', 'notes', 'price_per'];
      
      chargeFields.forEach(field => {
        if (!isEqual(currentCharge[field], previousCharge[field])) {
          result.chargeChanges.push({
            id: `${currentCharge.id}-${field}`,
            planId: currentPlan.id,
            planName: currentPlan.display_name || currentPlan.plan_name || 'Unnamed plan',
            chargeType: currentCharge.type,
            path: `${basePath}.charges.${currentCharge.id}.${field}`,
            previousValue: previousCharge[field],
            currentValue: currentCharge[field],
            changeType: 'modified'
          });
        }
      });
      
      // FUNDAMENTAL PROBLEM: The charge objects themselves don't store version information directly
      // Instead, versions are tracked at the page level in quarters.ids arrays
      // This requires a completely different approach
      
      // 1. First collect all versions/quarters where this charge appears in both current and previous extracts
      const findVersionsForCharge = (chargeId: string, company: any): Set<string> => {
        const allVersions = new Set<string>();
        
        if (!company || !company.pages) return allVersions;
        
        // Search all pages
        for (const page of company.pages) {
          // Look through quarters for this charge's ID
          if (page.quarters && Array.isArray(page.quarters)) {
            for (const quarter of page.quarters) {
              if (quarter.ids && Array.isArray(quarter.ids) && quarter.ids.includes(chargeId)) {
                // Add both quarter and version identifiers
                if (quarter.quarter) allVersions.add(quarter.quarter);
                if (quarter.version) allVersions.add(quarter.version);
              }
            }
          }
          
          // Some newer data might store versions directly on the charge
          if (page.plans && Array.isArray(page.plans)) {
            for (const plan of page.plans) {
              if (plan.charges && Array.isArray(plan.charges)) {
                const targetCharge = plan.charges.find((c: any) => c && c.id === chargeId);
                if (targetCharge && targetCharge.versions && Array.isArray(targetCharge.versions)) {
                  for (const version of targetCharge.versions) {
                    // Handle both string and object versions
                    if (typeof version === 'string') {
                      allVersions.add(version);
                    } else if (version && typeof version === 'object') {
                      if (version.quarter) allVersions.add(version.quarter);
                      if (version.version) allVersions.add(version.version);
                    }
                  }
                }
              }
            }
          }
        }
        
        return allVersions;
      };
      
      // 2. Get all versions for current and previous charge
      const currentVersions = findVersionsForCharge(currentCharge?.id || '', currentCompany);
      const previousVersions = findVersionsForCharge(previousCharge?.id || '', previousCompany);
      
      // Debug output all versions found
      console.log(`[DEBUG] Versions for charge ${currentCharge ? currentCharge.id : 'unknown'}:`, {
        currentVersionsFound: [...currentVersions],
        previousVersionsFound: [...previousVersions],
        currentCompanyHasPages: currentCompany?.pages?.length || 0,
        previousCompanyHasPages: previousCompany?.pages?.length || 0
      });
      
      // 4. Check for added versions
      const addedVersions = [...currentVersions].filter(v => !previousVersions.has(v));
      if (addedVersions.length > 0) {
        console.log(`Version diff detected: Added ${addedVersions.join(', ')} to charge ${currentCharge.id}`);
        result.chargeChanges.push({
          id: `${currentCharge.id}-versions-added`,
          planId: currentPlan.id,
          planName: currentPlan.display_name || currentPlan.plan_name || 'Unnamed plan',
          chargeType: currentCharge.type,
          path: `${basePath}.charges.${currentCharge.id}.versions`,
          previousValue: [...previousVersions],
          currentValue: [...currentVersions],
          changeType: 'modified',
          versionChange: {
            added: addedVersions as string[],
            removed: [] as string[]
          }
        });
      }
      
      // 5. Check for removed versions
      const removedVersions = [...previousVersions].filter(v => !currentVersions.has(v));
      if (removedVersions.length > 0) {
        console.log(`Version diff detected: Removed ${removedVersions.join(', ')} from charge ${currentCharge.id}`);
        result.chargeChanges.push({
          id: `${currentCharge.id}-versions-removed`,
          planId: currentPlan.id,
          planName: currentPlan.display_name || currentPlan.plan_name || 'Unnamed plan',
          chargeType: currentCharge.type,
          path: `${basePath}.charges.${currentCharge.id}.versions`,
          previousValue: [...previousVersions],
          currentValue: [...currentVersions],
          changeType: 'modified',
          versionChange: {
            added: [] as string[],
            removed: removedVersions as string[]
          }
        });
      }
    }
  });
  
  // Find removed charges
  previousCharges.forEach((previousCharge: any) => {
    if (!currentChargeMap.has(previousCharge.id)) {
      result.chargeChanges.push({
        id: previousCharge.id,
        planId: previousPlan.id,
        planName: previousPlan.display_name || previousPlan.plan_name || 'Unnamed plan',
        chargeType: previousCharge.type,
        path: `${basePath}.charges.${previousCharge.id}`,
        previousValue: previousCharge,
        currentValue: null,
        changeType: 'removed'
      });
    }
  });
  
  result.summary.hasChargesChanges = result.chargeChanges.length > 0;
};

/**
 * Format a structured diff into a human-readable format for display
 * @param diff The structured diff to format
 * @returns A formatted object with sections and items ready for display
 */
export const formatExtractDiffForDisplay = (diff: StructuredExtractDiff): {
  title: string;
  sections: {
    title: string;
    count: number;
    items: Array<{
      label: string;
      detail: string;
      type: 'added' | 'removed' | 'modified';
    }>;
  }[];
  totalChanges: number;
} => {
  const result = {
    title: 'Changes Summary',
    sections: [] as Array<{
      title: string;
      count: number;
      items: Array<{
        label: string;
        detail: string;
        type: 'added' | 'removed' | 'modified';
      }>;
    }>,
    totalChanges: diff.summary.totalChanges
  };

  // Company changes
  if (diff.companyChanges.length > 0) {
    const items = diff.companyChanges.map(change => {
      let detail = '';
      
      if (change.previousValue === null) {
        detail = `Added: ${formatValue(change.currentValue)}`;
      } else if (change.currentValue === null) {
        detail = `Removed: ${formatValue(change.previousValue)}`;
      } else {
        detail = `Changed from: ${formatValue(change.previousValue)} to: ${formatValue(change.currentValue)}`;
      }
      
      return {
        label: formatFieldName(change.field),
        detail,
        type: ((change.previousValue === null) ? 'added' : 
              (change.currentValue === null) ? 'removed' : 'modified') as 'added' | 'removed' | 'modified'
      };
    });
    
    result.sections.push({
      title: 'Company Information',
      count: items.length,
      items
    });
  }

  // Page changes
  if (diff.pageChanges.length > 0) {
    const items = diff.pageChanges.map(change => {
      const pageName = change.name || 'Page';
      let detail = '';
      
      if (change.changeType === 'added') {
        detail = `Added page: ${pageName}`;
      } else if (change.changeType === 'removed') {
        detail = `Removed page: ${pageName}`;
      } else {
        const pathParts = change.path.split('.');
        const field = pathParts[pathParts.length - 1];
        detail = `Changed ${formatFieldName(field)} from: ${formatValue(change.previousValue)} to: ${formatValue(change.currentValue)}`;
      }
      
      return {
        label: pageName,
        detail,
        type: change.changeType as 'added' | 'removed' | 'modified'
      };
    });
    
    result.sections.push({
      title: 'Pages',
      count: items.length,
      items
    });
  }

  // Pricing metrics changes
  if (diff.metricChanges.length > 0) {
    const items = diff.metricChanges.map(change => {
      const metricName = change.displayName || 'Pricing Metric';
      let detail = '';
      
      if (change.changeType === 'added') {
        detail = `Added pricing metric: ${metricName}`;
      } else if (change.changeType === 'removed') {
        detail = `Removed pricing metric: ${metricName}`;
      } else {
        const pathParts = change.path.split('.');
        const field = pathParts[pathParts.length - 1];
        detail = `Changed ${formatFieldName(field)} from: ${formatValue(change.previousValue)} to: ${formatValue(change.currentValue)}`;
      }
      
      return {
        label: metricName,
        detail,
        type: change.changeType as 'added' | 'removed' | 'modified'
      };
    });
    
    result.sections.push({
      title: 'Pricing Metrics',
      count: items.length,
      items
    });
  }

  // Plan changes
  if (diff.planChanges.length > 0) {
    const items = diff.planChanges.map(change => {
      const planName = change.planName || 'Plan';
      let detail = '';
      
      if (change.changeType === 'added') {
        detail = `Added plan: ${planName}`;
      } else if (change.changeType === 'removed') {
        detail = `Removed plan: ${planName}`;
      } else {
        const pathParts = change.path.split('.');
        const field = pathParts[pathParts.length - 1];
        detail = `Changed ${formatFieldName(field)} from: ${formatValue(change.previousValue)} to: ${formatValue(change.currentValue)}`;
      }
      
      return {
        label: planName,
        detail,
        type: change.changeType as 'added' | 'removed' | 'modified'
      };
    });
    
    result.sections.push({
      title: 'Plans',
      count: items.length,
      items
    });
  }

  // Charge changes
  if (diff.chargeChanges.length > 0) {
    const items = diff.chargeChanges.map(change => {
      const planName = change.planName || 'Plan';
      const chargeType = change.chargeType ? ` (${change.chargeType})` : '';
      let detail = '';
      
      if (change.changeType === 'added') {
        detail = `Added charge to ${planName}${chargeType}`;
      } else if (change.changeType === 'removed') {
        detail = `Removed charge from ${planName}${chargeType}`;
      } else if (change.versionChange) {
        // Special case for version assignments
        if (change.versionChange.added.length > 0) {
          const versions = change.versionChange.added.join(', ');
          detail = `Assigned charge to version(s): ${versions} in ${planName}${chargeType}`;
        } else if (change.versionChange.removed.length > 0) {
          const versions = change.versionChange.removed.join(', ');
          detail = `Removed charge from version(s): ${versions} in ${planName}${chargeType}`;
        }
      } else {
        const pathParts = change.path.split('.');
        const field = pathParts[pathParts.length - 1];
        detail = `Changed ${formatFieldName(field)} from: ${formatValue(change.previousValue)} to: ${formatValue(change.currentValue)} in ${planName}${chargeType}`;
      }
      
      return {
        label: `Charge in ${planName}${chargeType}`,
        detail,
        type: change.changeType as 'added' | 'removed' | 'modified'
      };
    });
    
    result.sections.push({
      title: 'Charges',
      count: items.length,
      items
    });
  }

  return result;
};

/**
 * Helper function to format field names for display
 */
const formatFieldName = (field: string): string => {
  // Convert snake_case to Title Case
  return field
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Helper function to format values for display
 */
const formatValue = (value: any): string => {
  if (value === null || value === undefined) {
    return 'None';
  }
  
  if (typeof value === 'object') {
    return '[Complex Value]';
  }
  
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  
  return String(value);
};

/**
 * Generate a summary of differences between two objects
 * @returns An object with counts of added, modified, and removed entities
 */
export const generateChangeSummary = (current: any, previous: any): { 
  added: string[];
  modified: string[];
  removed: string[];
  addedCount: number;
  modifiedCount: number;
  removedCount: number;
  totalChanges: number;
} => {
  // Generate differences only if both objects exist
  if (!current || !previous) {
    return {
      added: [],
      modified: [],
      removed: [],
      addedCount: 0,
      modifiedCount: 0,
      removedCount: 0,
      totalChanges: 0
    };
  }

  // Find all differences
  return findDifferences(current, previous);
};

/**
 * Fetch the latest extract content by slug
 */
export const fetchLatestExtract = async (slug: string): Promise<any> => {
  if (!slug) return null;
  
  try {
    const EXTRACT_QUERY = gql`
      query GetExtract($slug: String!) {
        extract(slug: $slug, timestamp: "latest") {
          slug
          timestamp
          json
          versions
        }
      }
    `;
    
    console.log(`Fetching latest extract content for slug: ${slug}`);
    const result = await graphqlClient.request<ExtractResponse>(EXTRACT_QUERY, { slug });
    
    if (result?.extract?.json) {
      try {
        // Parse the JSON content
        const extractData = JSON.parse(result.extract.json);
        return extractData;
      } catch (parseError) {
        console.error('Error parsing extract JSON:', parseError);
        return null;
      }
    } else {
      console.log('No extract data found or empty JSON response');
      return null;
    }
  } catch (error) {
    console.error('Error fetching extract:', error);
    return null;
  }
};

/**
 * Fetch the list of extract timestamps for a given slug
 */
export const fetchExtractTimestamps = async (slug: string): Promise<string[]> => {
  if (!slug) {
    console.log('No slug available for fetching extract timestamp');
    return [];
  }
  
  try {
    const EXTRACTS_QUERY = gql`
      query GetExtracts($slug: String!) {
        extracts(slug: $slug)
      }
    `;
    
    console.log(`Sending extracts GraphQL query for slug: ${slug}`);
    const result = await graphqlClient.request<ExtractsResponse>(EXTRACTS_QUERY, { slug });
    console.log('GraphQL extract response:', result);
    
    if (result?.extracts && result.extracts.length > 0) {
      // Sort timestamps in descending order (newest first)
      const sortedTimestamps = [...result.extracts].sort((a, b) => parseInt(b) - parseInt(a));
      return sortedTimestamps;
    }
    
    return [];
  } catch (error) {
    console.error('Failed to fetch extract timestamps:', error);
    return [];
  }
};

/**
 * Format a timestamp into a human-readable date string
 */
export const formatExtractDate = (timestamp: string): string => {
  const timestampNumber = parseInt(timestamp);
  const date = new Date(timestampNumber * 1000); // Convert Unix timestamp to milliseconds
  
  // Format the date in a human-readable format
  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
  
  return formatter.format(date);
};
